import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { STATUS_COLORS, displayStatus } from './invitationStatus';

/** What every invitation row carries, the platform's and an organisation's alike. */
export interface InvitationTableRow {
  id: number;
  email: string;
  name: string | null;
  status: string;
  created_at: string;
  expires_at: string;
}

export const formatInvitationDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleString() : '—';

/** The status chip; a lapsed PENDING reads EXPIRED. */
export const InvitationStatusTag: React.FC<{ invitation: InvitationTableRow }> = ({ invitation }) => {
  const word = displayStatus(invitation);
  return <Tag color={STATUS_COLORS[word] ?? 'default'}>{word}</Tag>;
};

interface InvitationTableProps<T extends InvitationTableRow> {
  rows: T[];
  loading?: boolean;
  /** The heading of the "what is it for" column: "Target" on the platform, "Where" on an Access page. */
  whereTitle: string;
  renderWhere: (row: T) => React.ReactNode;
  /** Shown as an "Invited by" column when given. */
  renderInvitedBy?: (row: T) => React.ReactNode;
  /** Shown as an "Actions" column when given. */
  renderActions?: (row: T) => React.ReactNode;
}

/**
 * The invitation table shared by the platform's Invitations page and the organisation and
 * entity Access pages: invitee, where, (invited by), status chip, dates, (actions).
 */
function InvitationTable<T extends InvitationTableRow>({
  rows,
  loading,
  whereTitle,
  renderWhere,
  renderInvitedBy,
  renderActions
}: InvitationTableProps<T>) {
  const columns: ColumnsType<T> = [
    {
      title: 'Invitee',
      key: 'invitee',
      render: (_, record) => (
        <span>
          <Typography.Text
            strong
            style={{ display: 'block' }}
          >{record.name || record.email}</Typography.Text>
          {record.name && <Typography.Text
            type="secondary"
          >{record.email}</Typography.Text>}
        </span>
      )
    }, {
      title: whereTitle,
      key: 'where',
      render: (_, record) => renderWhere(record)
    }, ...(renderInvitedBy ? [{
      title: 'Invited by',
      key: 'invited_by',
      render: (_: unknown, record: T) => renderInvitedBy(record)
    }] : []), {
      title: 'Status',
      key: 'status',
      render: (_, record) => <InvitationStatusTag invitation={record} />
    }, {
      title: 'Created',
      key: 'created_at',
      render: (_, record) => formatInvitationDate(record.created_at)
    }, {
      title: 'Expires',
      key: 'expires_at',
      render: (_, record) => formatInvitationDate(record.expires_at)
    }, ...(renderActions ? [{
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: T) => renderActions(record)
    }] : [])
  ];
  return (
    <Table<T>
      rowKey="id"
      loading={loading}
      dataSource={rows}
      columns={columns}
    />
  );
}

export default InvitationTable;
